// src/api/axiosClient.js
import axios from 'axios';


// ============================================
// CONFIGURATION
// ============================================

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000,
});

// ============================================
// TOKEN MANAGEMENT
// ============================================

class TokenManager {
  constructor() {
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  }

  getAccessToken() {
    return sessionStorage.getItem('access_token');
  }

  getRefreshToken() {
    // Login/OTP set the refresh token as an HttpOnly cookie — JS cannot read it.
    // Registration still returns refresh_token in the body (stored in localStorage).
    // Return the body token if present and valid; otherwise return a sentinel so
    // guards know the HttpOnly cookie exists (browser sends it via withCredentials).
    const bodyToken = localStorage.getItem('refresh_token');
    if (bodyToken && bodyToken !== 'null') return bodyToken;
    // Cookie-based session: return sentinel if an access token is active
    return sessionStorage.getItem('access_token') ? 'cookie' : null;
  }

  setTokens(accessToken, refreshToken, expiresIn) {
    // access token → sessionStorage (short-lived, cleared on tab close)
    if (accessToken) {
      sessionStorage.setItem('access_token', accessToken);
    }
    // Only persist body-provided refresh tokens (register flow).
    // Login sends null — the HttpOnly cookie is already set by the backend.
    if (refreshToken && refreshToken !== 'null') {
      localStorage.setItem('refresh_token', refreshToken);
    }
    const expiresAt = Date.now() + (expiresIn * 1000);
    sessionStorage.setItem('token_expires_at', expiresAt.toString());
  }

  clearTokens() {
    sessionStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');   // FIX-11
    sessionStorage.removeItem('token_expires_at');
  }

  isTokenExpiringSoon() {
    const expiresAt = sessionStorage.getItem('token_expires_at');
    // BUG FIX: return false (not true) when no expiry is stored.
    //
    // The previous code returned true on a missing value, treating "unknown"
    // as "expired". Every request then triggered a proactive token refresh in
    // the request interceptor, which blocked the original request until the
    // refresh completed or the 30-second axios timeout fired — causing the
    // "timeout of 30000ms exceeded" error seen on POST /orders/.
    //
    // The backend now includes expires_in in all token responses (login,
    // OTP verify, and refresh), so token_expires_at will be populated after
    // any successful auth flow. If it is somehow absent, the safer assumption
    // is that the token is still valid; the response interceptor will handle
    // any resulting 401 reactively.
    if (!expiresAt) return false;
    
    const expiresAtMs = parseInt(expiresAt, 10);
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    return expiresAtMs - now < fiveMinutes;
  }

  async refreshAccessToken() {
    if (this.isRefreshing) {
      // C-5 FIX: return a real Promise that resolves via the subscriber queue.
      // Previously callers pushed only a resolve callback, so on refresh failure
      // the promises were never settled — hanging requests indefinitely.
      return new Promise((resolve, reject) => {
        this.refreshSubscribers.push({ resolve, reject });
      });
    }
  
    this.isRefreshing = true;
  
    try {
      const refreshToken = this.getRefreshToken();
  
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Cookie-based sessions (login/OTP): send empty body — the browser
      // includes the HttpOnly cookie automatically via withCredentials.
      // Body-based sessions (register): send the token explicitly.
      const body = refreshToken !== 'cookie'
        ? { refresh_token: refreshToken }
        : {};

      // FIX: Use the configured `api` instance, not bare `axios`.
      // The bare axios instance does not inherit withCredentials: true, so for
      // cookie-based sessions (login/OTP) the HttpOnly refresh token cookie was
      // silently omitted from the request. The backend received an empty body
      // and no cookie, causing refresh_token to be None server-side and crashing
      // with 'NoneType' object has no attribute 'rsplit'.
      const response = await api.post('/auth/refresh', body);
  
      const { access_token, refresh_token, expires_in } = response.data;
  
      this.setTokens(access_token, refresh_token, expires_in);
  
      // C-5 FIX: resolve all waiting subscribers with the new token
      this.refreshSubscribers.forEach(({ resolve }) => resolve(access_token));
      this.refreshSubscribers = [];
  
      return access_token;
    } catch (error) {
      // C-5 FIX: reject all waiting subscribers so their callers get a real
      // rejection instead of hanging forever as unresolved promises.
      this.refreshSubscribers.forEach(({ reject }) => reject(error));
      this.refreshSubscribers = [];

      this.clearTokens();
      window.dispatchEvent(new CustomEvent('auth:token-refresh-failed'));
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }
  
}

const tokenManager = new TokenManager();

// ============================================
// REQUEST INTERCEPTOR
// ============================================

api.interceptors.request.use(
  async (config) => {
    // ✅ FIX: Don't refresh tokens for auth endpoints
    const isAuthEndpoint = 
      config.url?.includes('/auth/login') || 
      config.url?.includes('/auth/register') ||
      config.url?.includes('/auth/refresh') ||
      config.url?.includes('/auth/password-reset') ||
      config.url?.includes('/auth/verify-email') ||
      config.url?.includes('/auth/otp/');
    
    // Only proactively refresh for authenticated requests
    if (!isAuthEndpoint) {
      const hasRefreshToken = !!tokenManager.getRefreshToken();
      const isExpiring = tokenManager.isTokenExpiringSoon();
      
      if (hasRefreshToken && isExpiring) {
        try {
          await tokenManager.refreshAccessToken();
        } catch (error) {
          // Silent fail - response interceptor will handle 401
          console.warn('Proactive token refresh failed:', error);
        }
      }
    }

    // Add access token to all requests (except auth endpoints)
    if (!isAuthEndpoint) {
      const token = tokenManager.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================

api.interceptors.response.use(
  (response) => {
    const requestId = response.headers['x-request-id'];
    if (requestId) {
      response.requestId = requestId;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.headers?.['x-request-id']) {
      error.requestId = error.response.headers['x-request-id'];
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't retry auth endpoints
      const isAuthEndpoint = 
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/verify-email') ||
        originalRequest.url?.includes('/auth/verify-otp') ||
        originalRequest.url?.includes('/auth/otp/');

      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await tokenManager.refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Handle 429 Rate Limit
    if (error.response?.status === 429) {
      error.userMessage = 'Too many requests. Please try again in a moment.';
    }

    // Handle 409 Conflict
    if (error.response?.status === 409) {
      error.userMessage = error.response.data?.detail || 'Conflict error';
    }

    // Handle validation errors
    if (error.response?.status === 400) {
      error.userMessage = error.response.data?.detail || 'Invalid request';
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      error.userMessage = 'Server error. Please try again later.';
    }

    // Network errors
    if (!error.response) {
      error.userMessage = 'Network error. Please check your connection.';
    }

    return Promise.reject(error);
  }
);

// ============================================
// LOGOUT HELPER
// ============================================

export const logoutUser = async () => {
  // SEC-04 fix: include the refresh token in the body so the backend can add
  // its JTI to the Redis blacklist. Cookie-based sessions send 'cookie' as the
  // sentinel — the actual token travels automatically via withCredentials.
  // localStorage-based sessions (legacy register flow) send the literal token.
  const refreshToken = tokenManager.getRefreshToken();
  const body = (refreshToken && refreshToken !== 'cookie')
    ? { refresh_token: refreshToken }
    : {};

  try {
    await api.post('/auth/logout', body);
  } catch (error) {
    // Always clear local state even if the API call fails, so the user is
    // logged out client-side. The token will expire naturally server-side.
    console.error('Logout API call failed', error);
  } finally {
    tokenManager.clearTokens();
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
};

// ============================================
// EXPORTS
// ============================================

export { tokenManager };
export default api;